import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSales } from "@/contexts/SalesContext";

/**
 * In-app pricing page. Each tier ships the same "Profissional" features
 * (PDF/AEP unlock + empresas/avaliações ilimitadas) — what changes is the
 * size of the workforce the plan supports. Above 250 lives we direct the
 * lead to WhatsApp instead of a checkout flow.
 */
export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [accessDays, setAccessDays] = useState(30);
  const [busy, setBusy] = useState(null);
  // The Sollute SaaS sells annual-only since Feb/2026. Monthly subscription was
  // discontinued (backend transparently coerces "monthly" → "annual" to avoid 400).
  const cycle = "annual";
  const isAnnual = true;
  const { user, refresh } = useAuth();
  const { whatsappUrl } = useSales();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/pricing/plans")
      .then((r) => { setPlans(r.data.plans); setAccessDays(r.data.access_days); })
      .catch((e) => toast.error(e.response?.data?.detail || e.message));
  }, []);

  // Refresh user on mount so the "ativo" badge appears right after a success
  // redirect, without forcing a hard reload.
  useEffect(() => { refresh?.(); }, [refresh]);

  const buy = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
        billing_cycle: cycle,
      });
      if (!data.url) throw new Error("Stripe não retornou a URL de checkout");
      window.location.href = data.url; // hard redirect to Stripe
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/payments/customer-portal", {
        return_url: window.location.href,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
      setBusy(null);
    }
  };

  const isPaidActive = Boolean(user && !user.is_trial && user.paid_until);
  const paidUntil = isPaidActive ? new Date(user.paid_until).toLocaleDateString("pt-BR") : null;

  return (
    <div className="p-8 lg:p-12 max-w-6xl mx-auto" data-testid="pricing-page">
      <div className="mb-10">
        <div className="overline mb-2">Planos</div>
        <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">Escolha pelo número de vidas</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          Todos os planos liberam <strong>geração ilimitada de relatórios AEP em PDF</strong>, número
          ilimitado de empresas e avaliações, OCR, dashboard e plano de ação. A diferença é apenas o
          tamanho da força de trabalho avaliada.
        </p>
        <p className="text-sm text-emerald-800 mt-4 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-3 py-2">
          <i className="fa-solid fa-credit-card"></i>
          <span><strong>Plano anual com pagamento em até 12x sem juros no cartão.</strong> Renovação após 12 meses para manter o acesso ativo.</span>
        </p>
      </div>

      {isPaidActive && (
        <div className="card mb-8 border-emerald-200 bg-emerald-50" data-testid="pricing-active-banner">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-circle-check text-emerald-700 text-2xl"></i>
              <div>
                <div className="font-bold text-emerald-900">Plano ativo · {user.plan_label || user.plan_tier}</div>
                <div className="text-xs text-emerald-900/80">Acesso liberado até {paidUntil}.</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {user.stripe_customer_id && (
                <button onClick={openPortal} disabled={busy === "portal"}
                        className="btn btn-secondary text-sm" data-testid="pricing-manage-subscription">
                  {busy === "portal"
                    ? <><i className="fa-solid fa-circle-notch fa-spin"></i> Abrindo…</>
                    : <><i className="fa-solid fa-gear"></i> Gerenciar assinatura</>}
                </button>
              )}
              <button onClick={() => navigate("/app")} className="btn btn-primary text-sm" data-testid="pricing-go-to-app">
                <i className="fa-solid fa-arrow-right"></i> Ir ao painel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((p) => {
          const annualTotal = p.amount * 12;
          const installment = annualTotal / 12;
          return (
            <div key={p.id} className="card flex flex-col" data-testid={`plan-card-${p.id}`}>
              <div className="overline text-emerald-900 mb-2">{p.label}</div>

              <div className="flex items-baseline gap-1">
                <span className="text-xs text-slate-500">12x de</span>
                <span className="text-3xl font-display font-extrabold text-slate-900">
                  R$ {installment.toFixed(2).replace(".", ",")}
                </span>
                <span className="text-xs text-emerald-700 font-bold uppercase tracking-wider ml-1">sem juros</span>
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-3">
                Total R$ {annualTotal.toFixed(2).replace(".", ",")} · acesso por 12 meses
              </div>

              <ul className="text-sm text-slate-600 space-y-2 mb-6 flex-1">
                <li><i className="fa-solid fa-check text-emerald-700 mr-2"></i> Até {p.max_lives} colaboradores</li>
                <li><i className="fa-solid fa-check text-emerald-700 mr-2"></i> AEP em PDF ilimitado</li>
                <li><i className="fa-solid fa-check text-emerald-700 mr-2"></i> Empresas e avaliações ilimitadas</li>
                <li><i className="fa-solid fa-check text-emerald-700 mr-2"></i> OCR + Dashboard + Plano de ação</li>
                <li><i className="fa-solid fa-check text-emerald-700 mr-2"></i> Pague em até 12x no cartão de crédito</li>
              </ul>
              <button
                onClick={() => buy(p.id)}
                disabled={busy === p.id}
                data-testid={`plan-cta-${p.id}`}
                className="btn btn-primary w-full justify-center">
                {busy === p.id
                  ? <><i className="fa-solid fa-circle-notch fa-spin"></i> Indo ao checkout…</>
                  : <><i className="fa-solid fa-credit-card"></i> Assinar 12x sem juros</>}
              </button>
            </div>
          );
        })}

        {/* Enterprise — WhatsApp lead */}
        <div className="card flex flex-col bg-emerald-950 text-white border-emerald-900" data-testid="plan-card-enterprise">
          <div className="overline text-[#8BC53F] mb-2">Acima de 250 vidas</div>
          <div className="text-2xl font-display font-extrabold mb-3">Sob consulta</div>
          <ul className="text-sm text-emerald-100/90 space-y-2 mb-6 flex-1">
            <li><i className="fa-solid fa-check text-[#8BC53F] mr-2"></i> Tudo dos planos anteriores</li>
            <li><i className="fa-solid fa-check text-[#8BC53F] mr-2"></i> Atendimento dedicado</li>
            <li><i className="fa-solid fa-check text-[#8BC53F] mr-2"></i> Múltiplos avaliadores com RBAC</li>
            <li><i className="fa-solid fa-check text-[#8BC53F] mr-2"></i> Acordo comercial customizado</li>
          </ul>
          <a
            href={whatsappUrl("Olá! Tenho mais de 250 colaboradores e quero conhecer o plano corporativo do Sollute NR-01.")}
            target="_blank" rel="noreferrer"
            data-testid="plan-cta-enterprise"
            className="btn bg-[#8BC53F] text-emerald-950 hover:bg-white w-full justify-center">
            <i className="fa-brands fa-whatsapp"></i> Falar com comercial
          </a>
        </div>
      </div>

      <div className="card mt-8 border-slate-200">
        <div className="overline mb-2">Como funciona o pagamento</div>
        <ul className="text-sm text-slate-600 space-y-2 list-disc pl-5">
          <li>Você é redirecionado para o <strong>checkout seguro do Stripe</strong> (cartão de crédito).</li>
          <li><strong>Plano Anual:</strong> pagamento único do valor total, com <strong>parcelamento em até 12x sem juros</strong> no cartão (sujeito ao banco emissor).</li>
          <li>O acesso é liberado por <strong>12 meses</strong> a partir da confirmação do pagamento. Após esse período é necessário renovar para continuar usando.</li>
          <li>Você pode cancelar a operação a qualquer momento antes de confirmar no Stripe — nada é cobrado.</li>
        </ul>
      </div>
    </div>
  );
}
